import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { WS_NAMESPACE, type WsEvent } from '@war-game/contracts';
import { io, type Socket } from 'socket.io-client';
import { STANDALONE } from '../platform';
import { useAuth } from '../store/auth';
import { keys } from './queries';

let socket: Socket | null = null;
let currentMatch: string | null = null;

export type Notice = { at: number; event: WsEvent; payload: Record<string, unknown> };
const listeners = new Set<(n: Notice) => void>();

export function onNotice(fn: (n: Notice) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * WebSocket (namespace /game). Os eventos são notificações: invalidam as queries
 * e o cliente relê via REST. Ao reconectar, invalida tudo.
 */
type On = (event: WsEvent, handler: (payload: Record<string, unknown>) => void) => void;

let localBound = false;

/** Standalone: os mesmos eventos, vindos do servidor local no Web Worker. */
function connectLocal(qc: QueryClient): void {
  if (localBound) return;
  localBound = true;
  const handlers = new Map<WsEvent, Array<(payload: Record<string, unknown>) => void>>();
  bindEvents(qc, (event, handler) => handlers.set(event, [...(handlers.get(event) ?? []), handler]));
  void import('./local').then(({ localClient }) =>
    localClient().on((event, payload) => {
      for (const h of handlers.get(event) ?? []) h(payload);
      for (const l of listeners) l({ at: Date.now(), event, payload });
    }),
  );
}

export function connectSocket(qc: QueryClient): Socket | null {
  if (STANDALONE) {
    connectLocal(qc);
    return null;
  }
  if (socket) return socket;
  socket = io(WS_NAMESPACE, {
    transports: ['websocket'],
    auth: (cb) => cb({ token: useAuth.getState().token }),
    reconnectionDelayMax: 10_000,
  });
  socket.on('connect', () => {
    if (currentMatch) socket?.emit('join', { matchId: currentMatch });
    void qc.invalidateQueries();
  });
  bindEvents(qc, (event, handler) => {
    socket!.on(event, (payload: Record<string, unknown>) => {
      handler(payload);
      for (const l of listeners) l({ at: Date.now(), event, payload });
    });
  });
  return socket;
}

/** Janela em que uma rajada de notificações vira uma só releitura por query. */
const BURST_MS = 250;

/**
 * Invalidação em lote: a virada (e cada ação) manda dezenas de notificações seguidas — uma por
 * construção, produção, objetivo… Sem juntar, cada uma refaz a mesma leitura (e o servidor responde
 * 429 Too Many Requests). Aqui cada query é relida uma vez por rajada.
 */
export function batchedInvalidate(qc: QueryClient, delayMs = BURST_MS): (key: QueryKey) => void {
  const pending = new Map<string, QueryKey>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (key) => {
    pending.set(JSON.stringify(key), key);
    timer ??= setTimeout(() => {
      timer = null;
      const batch = [...pending.values()];
      pending.clear();
      for (const queryKey of batch) void qc.invalidateQueries({ queryKey });
    }, delayMs);
  };
}

/** Eventos são notificações: invalidam as queries e o cliente relê (HTTP ou servidor local). */
function bindEvents(qc: QueryClient, on: On): void {
  const matchId = (p: Record<string, unknown>) => String(p.matchId ?? currentMatch ?? '');
  const invalidate = batchedInvalidate(qc);
  on('turn:resolving', (p) => invalidate(keys.match(matchId(p))));
  on('turn:resolved', (p) => {
    const id = matchId(p);
    for (const k of [
      keys.match(id),
      keys.view(id),
      ['news', id],
      ['combats', id],
      keys.score(id),
      keys.research(id),
      keys.reports(id),
    ]) {
      invalidate(k);
    }
  });
  for (const event of [
    'state:changed',
    'timer:completed',
    'intel:report',
    'radar:contact',
    'diplomacy:update',
    'trade:offer',
    'objective:completed',
  ] as const) {
    on(event, (p) => {
      const id = matchId(p);
      invalidate(keys.view(id));
      if (event === 'intel:report') invalidate(keys.reports(id));
      if (event === 'timer:completed') invalidate(keys.research(id));
    });
  }
  on('news:edition', (p) => invalidate(['news', matchId(p)]));
  on('chat:message', (p) => invalidate(['messages', matchId(p)]));
  on('match:updated', (p) => {
    invalidate(keys.match(matchId(p)));
    invalidate(keys.matches);
  });
}

export function joinMatch(matchId: string | null): void {
  currentMatch = matchId;
  if (socket?.connected && matchId) socket.emit('join', { matchId });
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}
